// src/hooks/useSalonAiContext.ts
// Contesto AI del salone per il widget flottante: carica goals/compensi/costi
// e costruisce il contesto con la stessa logica della pagina Assistente.
import { useEffect, useMemo, useState } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { useEvents } from '@/contexts/EventsContext';
import { useClients } from '@/contexts/ClientsContext';
import { useUsers } from '@/contexts/UsersContext';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import { fetchGoalsService, Goal } from '@/services/goals/goalsService';
import { fetchCompensationsService, EmployeeCompensation } from '@/services/employees/employeeCompensationService';
import { fetchShopCostsService, ShopCost } from '@/services/shop/shopCostsService';
import { buildSalonContext, computeSalonTrends } from '@/services/assistant/salonContextService';

export const useSalonAiContext = (enabled: boolean) => {
    const { organization } = useAuth();
    const { bookings, busySlots, getPerformanceById, isLoading: eventsLoading } = useEvents();
    const { clients } = useClients();
    const { users } = useUsers();
    const { getValidBookings, calculateRevenue } = useAnalytics();

    const [goals, setGoals] = useState<Goal[]>([]);
    const [comps, setComps] = useState<EmployeeCompensation[]>([]);
    const [costs, setCosts] = useState<ShopCost[]>([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!enabled || !organization?.id || loaded) return;
        let active = true;
        (async () => {
            const shopId = Number(organization.id);
            const [g, c, s] = await Promise.all([
                fetchGoalsService(shopId),
                fetchCompensationsService(shopId),
                fetchShopCostsService(shopId),
            ]);
            if (!active) return;
            if (g.success) setGoals(g.data);
            if (c.success) setComps(c.data);
            if (s.success) setCosts(s.data);
            setLoaded(true);
        })();
        return () => { active = false; };
    }, [enabled, organization?.id, loaded]);

    const salonContext = useMemo(() => {
        const helpers = { getValidBookings, calculateRevenue, getPerformanceById };
        const trends = computeSalonTrends(bookings || [], clients || [], helpers);
        return buildSalonContext({
            organization,
            bookings: bookings || [],
            clients: clients || [],
            users: users || [],
            busySlots: busySlots || [],
            goals,
            comps,
            costs,
            trends,
            helpers,
        });
    }, [organization, bookings, clients, users, busySlots, goals, comps, costs, getValidBookings, calculateRevenue, getPerformanceById]);

    return { salonContext, ready: loaded && !eventsLoading };
};
